import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import api from "../../lib/api";
import ListPageLayout, { SkeletonGrid, EmptyState, FilterChip } from "../../components/smart/ListPageLayout";
import ResultCard from "../../components/smart/ResultCard";
import { EVENT_CAT_LABELS } from "../../components/map/markers";

const SORTS = [
  { value: "soon", label: "In arrivo" },
  { value: "featured", label: "In evidenza" },
];

export default function EventsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("soon");

  useEffect(() => {
    api.get("/public/events").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  }, []);

  const categories = useMemo(() => {
    if (!items) return [];
    return [...new Set(items.map((e) => e.category).filter(Boolean))].slice(0, 12);
  }, [items]);

  const shown = useMemo(() => {
    if (!items) return null;
    let list = items.slice();
    if (cat !== "all") list = list.filter((e) => e.category === cat);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((e) => (e.title || "").toLowerCase().includes(s) || (e.comune || e.location_name || "").toLowerCase().includes(s));
    }
    list.sort((a, b) => {
      if (sort === "featured") return (b.sponsored ? 1 : 0) - (a.sponsored ? 1 : 0) || (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      return (a.start_at || "").localeCompare(b.start_at || "");
    });
    return list;
  }, [items, cat, q, sort]);

  const chips = [
    <FilterChip key="all" testid="chip-all" active={cat === "all"} onClick={() => setCat("all")}>Tutti</FilterChip>,
    ...categories.map((k) => (
      <FilterChip key={k} testid={`chip-${k}`} active={cat === k} onClick={() => setCat(k)}>{EVENT_CAT_LABELS[k] || k}</FilterChip>
    )),
  ];

  const when = (e) => e.is_live ? "In corso ora" : (e.start_at ? new Date(e.start_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

  return (
    <ListPageLayout
      title="Eventi" subtitle="Sagre, concerti, feste e appuntamenti del Canavese" icon={CalendarDays}
      search={q} onSearch={setQ} searchPlaceholder="Cerca evento o comune…"
      sort={sort} onSort={setSort} sortOptions={SORTS} chips={chips}
    >
      {shown === null ? (
        <SkeletonGrid />
      ) : shown.length === 0 ? (
        <EmptyState icon={CalendarDays} message="Nessun evento in programma al momento." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="events-list">
          {shown.map((e) => (
            <ResultCard
              key={e.id}
              testid={`event-card-${e.id}`}
              image={e.image}
              fallbackIcon={CalendarDays}
              title={e.title}
              subtitle={`${EVENT_CAT_LABELS[e.category] || "Evento"}${e.comune ? ` · ${e.comune}` : ""}`}
              statusLabel={when(e)}
              statusType={e.is_live ? "open" : "arriving"}
              extraBadge={e.sponsored ? "Sponsor" : (e.featured ? "Evidenza" : null)}
              onOpen={() => navigate("/esplora")}
              onMap={() => navigate("/esplora")}
            />
          ))}
        </div>
      )}
    </ListPageLayout>
  );
}
